import {
	Button,
	HStack,
	Input,
	Select,
	SelectOptionType,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import { ACCOUNT_API_COMPANIES, SUBSCRIPTIONS_INVITE_USER } from 'apiRoutes';
import CountrySelect from 'components/common/CountrySelect';
import { RequestLoader } from 'components/common/Loader';
import { StandardModal } from 'components/common/Modal/StandardModal';
import { validateCountryCode } from 'components/companies/companyValidation';
import { Company } from 'domain/company/Company';
import { useGetOnMount } from 'hooks/useGetOnMount';
import useSubmit, { POST } from 'hooks/useSubmit';
import { useEffect, useState } from 'react';
import { RequestStatus } from 'types/RequestStatus';

interface InviteUserFormState {
	email: string;
	firstName: string;
	lastName: string;
	jobTitle: string;
	company: SelectOptionType | null;
	countryCode: string;
}

const initialFormState: InviteUserFormState = {
	email: '',
	firstName: '',
	lastName: '',
	jobTitle: '',
	countryCode: '',
	company: null,
};

const formStateToPayload = (
	formState: InviteUserFormState,
	subscriptionId: number,
) => ({
	subscriptionId: subscriptionId,
	email: formState.email,
	firstName: formState.firstName,
	lastName: formState.lastName,
	jobTitle: formState.jobTitle,
	companyId: formState.company!.value,
	countryCode: formState.countryCode,
});

export const InviteUserModal: React.FC<{
	setShowModal: (show: boolean) => void;
	showModal: boolean;
	subscriptionId: number;
	onUserInvited: () => void;
	companyId?: number;
}> = ({
	setShowModal,
	showModal,
	subscriptionId,
	onUserInvited,
	companyId,
}) => {
	const [formState, setFormState] =
		useState<InviteUserFormState>(initialFormState);
	const [errors, setErrors] = useState<
		Partial<Record<keyof InviteUserFormState, string>>
	>({});

	const { data: allCompanies, status: allCompaniesStatus } = useGetOnMount<
		Company[]
	>(`${ACCOUNT_API_COMPANIES}/GetCompanies`);

	const [inviteUser, inviteUserStatus] = useSubmit(
		`${SUBSCRIPTIONS_INVITE_USER}`,
		POST,
		{
			messageOnError: 'An error occurred while inviting user.',
			messageOnSuccess: 'User invited successfully.',
			showApiError: true,
		},
	);

	useEffect(() => {
		if (companyId && allCompanies) {
			const company = allCompanies.find((c) => c.id === companyId);

			if (company) {
				setFormState({
					...formState,
					company: { value: company.id, label: company.name },
					countryCode: company.country?.twoLetterIsoCode || '',
				});
			}
		}
	}, [companyId, allCompanies]);

	const onSubmit = async () => {
		validateNotEmpty(formState.firstName, 'firstName');
		validateNotEmpty(formState.lastName, 'lastName');
		validateNotEmpty(formState.jobTitle, 'jobTitle');
		validateNotEmpty(formState.email, 'email');
		validateNotEmpty(formState.countryCode, 'countryCode');
		validateEmail();
		validateCountryField();

		const payload = formStateToPayload(formState, subscriptionId);
		const result = await inviteUser(payload);

		if (result.success) {
			onUserInvited();
			setShowModal(false);
		}
	};

	const onCancel = () => {
		setFormState(initialFormState);
		setShowModal(false);
	};

	const onCountryChange = (countryCode: string) => {
		setFormState({
			...formState,
			countryCode: countryCode,
		});
	};

	const validateNotEmpty = (
		value: string,
		field: keyof InviteUserFormState,
	) => {
		setErrors({
			...errors,
			[field]: value ? undefined : `${field} is required`,
		});
	};

	const validateCountryField = () => {
		setErrors({
			...errors,
			countryCode: validateCountryCode(formState.countryCode),
		});
	};

	const validateEmail = () => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		setErrors({
			...errors,
			email: emailRegex.test(formState.email)
				? undefined
				: 'Invalid email address',
		});
	};

	const canSubmit =
		formState.email.length > 0 &&
		formState.company !== null &&
		formState.firstName.length > 0 &&
		formState.lastName.length > 0 &&
		formState.jobTitle.length > 0;

	return (
		<StandardModal
			isOpen={showModal}
			allowOverflow
			onClose={() => setShowModal(false)}
			header={'Invite user to subscription'}
			footer={
				<HStack justify="flex-end" fullWidth gap="md">
					<Button
						text="Invite"
						size="sm"
						type="submit"
						isLoading={inviteUserStatus === RequestStatus.LOADING}
						loadingText="Inviting user..."
						onClick={onSubmit}
						isDisabled={!canSubmit}
					/>
					<Button
						text="Cancel"
						size="sm"
						variant="secondary"
						onClick={onCancel}
					/>
				</HStack>
			}
		>
			<RequestLoader status={allCompaniesStatus}>
				<VStack gap="xl" fullWidth>
					<HStack fullWidth gap="md" wrap="nowrap">
						<Input
							label="First Name"
							placeholder="Enter first name"
							value={formState.firstName}
							required
							onChange={(e) =>
								setFormState({
									...formState,
									firstName: e?.target?.value || '',
								})
							}
						/>
						<Input
							label="Last Name"
							placeholder="Enter last name"
							value={formState.lastName}
							required
							onChange={(e) =>
								setFormState({
									...formState,
									lastName: e?.target?.value || '',
								})
							}
						/>
					</HStack>
					<HStack fullWidth gap="md" wrap="nowrap">
						<Input
							label="Job Title"
							placeholder="Enter job title"
							value={formState.jobTitle}
							required
							onChange={(e) =>
								setFormState({
									...formState,
									jobTitle: e?.target?.value || '',
								})
							}
						/>
						<Input
							label="Email"
							placeholder="Enter email"
							value={formState.email}
							required
							onChange={(e) =>
								setFormState({
									...formState,
									email: e?.target?.value || '',
								})
							}
						/>
					</HStack>
					<HStack fullWidth gap="md" wrap="nowrap">
						<Select
							label="Company"
							placeholder="Select a company"
							required
							value={formState.company}
							options={allCompanies?.map((company) => ({
								value: company.id,
								label: company.name,
							}))}
							onChange={(companyOption) => {
								if (companyOption) {
									setFormState({
										...formState,
										company: companyOption,
									});
								}
							}}
						/>
						<CountrySelect
							required
							value={formState.countryCode}
							onChange={onCountryChange}
						/>
					</HStack>
				</VStack>
			</RequestLoader>
		</StandardModal>
	);
};
